// @ts-nocheck
import React, { FC, useState } from 'react';
import { whichAuth as getAuth } from '../../getAuth';
import { showPoAState, userState, viewState } from '../../atoms';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { handleErrors } from '../../utils/handleErrors';
import { addOutline, checkmark, trashOutline } from 'ionicons/icons';
import { REST_API_URL, viewDashboard } from '../../constants/global';
import { PaymentInfo, ProviderSearch, UserSearch, AlertModal } from '../../components';
import {
  IonButton,
  IonCol,
  IonGrid,
  IonIcon,
  IonPage,
  IonProgressBar,
  IonRow,
  IonSpinner
} from '@ionic/react';
import { signupInProgress } from '../../utils/signupInProgress';
import { PageProps } from "../Page.model";
import './Providers.css'

const Providers: FC<PageProps> = ({ show }) => {
  const user = useRecoilValue(userState);
  const setUser = useSetRecoilState(userState);
  const showPoA = useSetRecoilState(showPoAState);
  const setView = useSetRecoilState(viewState);
  
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [isOpenError, setIsOpenError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [providerUser, setProviderUser] = useState<any>(null);
  const [providerConnecting, setProviderConnecting] = useState<String>(null);

  const addProvider = providerToAdd => {
    if(!user.providers.find(provider => provider.id === providerToAdd.id)) {
      providerToAdd.status = 'NEW';
      setUser({
        ...user,
        providers: [
          ...user.providers,
          providerToAdd
        ]
      });
      getAuth().currentUser.getIdToken().then(token =>
        fetch(REST_API_URL + '/invoiceRedirects/', {
          method: 'POST',
          headers: {
            'Authorization': 'Bearer ' + token,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            user: user._links.self.href,
            provider: providerToAdd._links.self.href,
            status: 'NEW'
          })
        })
          .then(handleErrors)
          .catch(() => {
            setErrorMessage('Failed to add provider')
            setIsOpenError(true)
            // undo
            setUser({
              ...user,
              providers: user.providers.filter(provider => provider.id !== providerToAdd.id)
            });
          })
      );
    }
  }
  
  const connectProvider = provider => {
    getAuth().currentUser.getIdToken().then(token => {
      setProviderConnecting(provider.id);
      fetch(REST_API_URL + '/invoiceRedirects/' + providerUser.id + '_' + provider.id, {
        method: 'PATCH',
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/merge-patch+json'
        },
        body: JSON.stringify({
          user: providerUser._links.self.href,
          provider: provider._links.self.href,
          status: 'OK'
        })
      })
      .then(handleErrors)
      .then(() => provider.status = 'OK')
      .catch(() => {
        setErrorMessage('Failed to connect provider')
        setIsOpenError(true)
      })
      .finally(() => setProviderConnecting(null));
    });
  }
  
  const removeProvider = providerToRemove => {
    setUser({
      ...user,
      providers: user.providers.filter(provider => provider.id !== providerToRemove.id)
    });
    getAuth().currentUser.getIdToken().then(token =>
      fetch(REST_API_URL + '/invoiceRedirects/' + user.id + '_' + providerToRemove.id, {
          method:'DELETE',
          headers: {'Authorization': 'Bearer ' + token}
        })
        .then(handleErrors)
        .catch(() => {
          setErrorMessage('Failed to remove provider')
          setIsOpenError(true)
          // undo remove
          setUser({
            ...user,
            providers: user.providers.push(providerToRemove)
          });
        })
    );
  }
  
  const progressValue = status => {
    switch(status) {
      case 'NEW': return .25
      case 'SIGNED': return .5
      case 'REQUESTED': return .75
      case 'OK': return 1
      default: return 0
    }
  }

  const addServiceRow = service => (
    <IonCol size="12" style={{ margin:'10px 0', border: '1px solid #ddd'}}>
      <IonRow>
        <IonCol style={{fontSize:'1rem',color:'#333',display:'flex', alignItems:'center'}}>{service}</IonCol>
        <IonCol size="auto">
          <IonButton fill="clear" onClick={() => setSelectedService(service)}>
            { service === selectedService ? <IonSpinner name="dots"/> : <span>Add provider <IonIcon icon={addOutline} /></span>}
          </IonButton>
        </IonCol>
      </IonRow>
      { (providerUser || user)?.providers?.filter(provider => service === provider?.service).map(provider => insertProviderRow(provider)) }
    </IonCol>
  );
  
  const insertProviderRow = provider => (
    <IonRow key={provider.id} style={{borderTop:'1px solid #ddd', marginTop:'10px', padding:'10px'}}>
      <IonCol size="auto" style={{maxWidth:'100px', maxHeight:'100px', display:'flex', alignItems:'center', justifyContent:'center'}}>
        <img src={provider.logo.replace(/upload/,'upload/w_60,h_60,c_fit').replace(/.svg$/, '.png')} alt="logo"/>
      </IonCol>
      <IonCol>
        <IonRow>
          <IonCol>
            <span style={{fontSize:'1rem',color:'black'}}>{provider.name}</span>
          </IonCol>
          <IonCol size="auto">
            { user.admin && provider.status === 'SIGNED' && 
              <IonButton size="small" disabled={!providerUser} onClick={() => connectProvider(provider)}>
                { (providerConnecting === provider.id) && <IonSpinner name="dots"/> } Connected
              </IonButton>
            }
            <IonButton size="small" fill="clear" disabled={provider.status !== 'NEW'}
              onClick={() => removeProvider(provider)}>
              <IonIcon icon={trashOutline} />
            </IonButton>
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol>
            <IonProgressBar color="primary" value={progressValue(provider.status)} />
            <small>status: <span style={{textTransform:'lowercase'}}>{provider.status}</span> {'OK' === provider.status && <IonIcon icon={checkmark} size="small" color="success" /> }</small>
          </IonCol>
        </IonRow>
      </IonCol>
    </IonRow> );
  
  return (show &&
    <IonPage className="providers-content">
      <PaymentInfo />
      <IonGrid className="providers-content-list">
        { user.admin ?
          <UserSearch onSelect={providerUser => setProviderUser(providerUser)} />
          :
          <ProviderSearch
            onSelect={provider => addProvider(provider)}
            selectedService={selectedService}
            clearSelectedService={() => setSelectedService(null)}
          />
        }
        <IonRow className="providers-content-list-content">
          { addServiceRow('Auto Insurance') }
          { addServiceRow('Car Registration') }
          { addServiceRow('Council Rates') }
          { addServiceRow('Electricity') }
          { addServiceRow('Fixed Line') }
          { addServiceRow('Fuel') }
          { addServiceRow('Gas') }
          { addServiceRow('Health Insurance') }
          { addServiceRow('Home/general insurance') }
          { addServiceRow('Internet') }
          { addServiceRow('Mobile') }
          { addServiceRow('Water') }
        </IonRow>
        <IonRow>
          <IonCol size="12">
            <IonButton
              className="providers-button"
              onClick={() => showPoA(true)}
              disabled={!user?.providers?.some(provider => provider?.status === 'NEW')} >
              Next
            </IonButton>
            { signupInProgress(user) &&
              <IonButton
                className="providers-button cancel"
                onClick={() => setView(viewDashboard)}>
                Cancel
              </IonButton>
            }
          </IonCol>
          <AlertModal
            isOpen={isOpenError}
            title={errorMessage}
            onClosed={() => setIsOpenError(false)}
          />
        </IonRow>
      </IonGrid>
    </IonPage>
  );
}

export default Providers;
